import { deleteAPI, getAPI, patchAPI, postAPI } from "./Networking";
import { SwayyAPIResponse } from "../../../models/NetworkResponse";
import {
  CancelSwayyOrderResult,
  CreateSwayyOrderNotePayload,
  GetSwayyOrdersParams,
  SwayyOrdersListData,
  UpdateSwayyOrderNotePayload,
} from "../../../models/SwayyOrder/SwayyOrder";
import {
  SwayyOrderDetail,
  SwayyOrderNote,
} from "../../../models/SwayyOrder/SwayyOrderDetail";

const unwrapNote = (
  payload: SwayyOrderNote | { note: SwayyOrderNote },
): SwayyOrderNote => {
  if ("note" in payload && payload.note && typeof payload.note === "object") {
    return payload.note as SwayyOrderNote;
  }
  return payload as SwayyOrderNote;
};

export default class SwayyOrderAPI {
  getQueryParams = (
    params: GetSwayyOrdersParams,
  ): Record<string, string | number> => {
    const query: Record<string, string | number> = {
      page: params.page,
      limit: params.limit,
    };
    if (params.q) query.q = params.q;
    if (params.status) query.status = params.status;
    if (params.payment_status) query.payment_status = params.payment_status;
    if (params.channel) query.channel = params.channel;
    if (params.sort_by) query.sort_by = params.sort_by;
    if (params.sort_order) query.sort_order = params.sort_order;
    if (params.start_date) query.start_date = params.start_date;
    if (params.end_date) query.end_date = params.end_date;
    return query;
  };

  getOrders = async (
    params: GetSwayyOrdersParams,
  ): Promise<SwayyOrdersListData> => {
    try {
      const response = (await getAPI(
        "/v1/orders/admin",
        this.getQueryParams(params),
      )) as SwayyAPIResponse<SwayyOrdersListData>;

      const payload = response.data ?? response.result;
      if (!response.success || !payload) {
        throw new Error(response.message || "Failed to fetch orders");
      }

      return {
        orders: payload.orders ?? [],
        pagination: {
          total: payload.pagination?.total ?? 0,
          page: payload.pagination?.page ?? params.page,
          limit: payload.pagination?.limit ?? params.limit,
        },
      };
    } catch (error) {
      throw error;
    }
  };

  getOrderDetail = async (
    orderId: number | string,
  ): Promise<SwayyOrderDetail> => {
    try {
      const response = (await getAPI(
        `/v1/orders/admin/${orderId}`,
      )) as SwayyAPIResponse<SwayyOrderDetail>;

      const payload = response.data ?? response.result;
      if (!response.success || !payload) {
        throw new Error(response.message || "Failed to fetch order details");
      }

      return {
        ...payload,
        items: payload.items ?? [],
        payments: payload.payments ?? [],
        shipments: payload.shipments ?? [],
        refunds: payload.refunds ?? [],
        timeline: payload.timeline ?? [],
        totals: payload.totals ?? {
          item_subtotal: 0,
          discount_total: 0,
          shipping_total: 0,
          cod_surcharge: 0,
          tax_total: 0,
          grand_total: 0,
        },
      };
    } catch (error) {
      throw error;
    }
  };

  getOrderNotes = async (
    orderId: number | string,
  ): Promise<SwayyOrderNote[]> => {
    try {
      const response = (await getAPI(
        `/v1/orders/admin/${orderId}/notes`,
      )) as SwayyAPIResponse<{ notes: SwayyOrderNote[] } | SwayyOrderNote[]>;

      const payload = response.data ?? response.result;
      if (!response.success || !payload) {
        throw new Error(response.message || "Failed to fetch notes");
      }

      if (Array.isArray(payload)) return payload;
      return payload.notes ?? [];
    } catch (error) {
      throw error;
    }
  };

  addOrderNote = async (
    orderId: number | string,
    body: CreateSwayyOrderNotePayload,
  ): Promise<SwayyOrderNote> => {
    try {
      const response = (await postAPI(
        `/v1/orders/admin/${orderId}/notes`,
        body,
      )) as SwayyAPIResponse<SwayyOrderNote | { note: SwayyOrderNote }>;

      const payload = response.data ?? response.result;
      if (!response.success || !payload) {
        throw new Error(response.message || "Failed to add note");
      }

      return unwrapNote(payload);
    } catch (error) {
      throw error;
    }
  };

  updateOrderNote = async (
    orderId: number | string,
    noteId: number | string,
    body: UpdateSwayyOrderNotePayload,
  ): Promise<SwayyOrderNote> => {
    try {
      const response = (await patchAPI(
        `/v1/orders/admin/${orderId}/notes/${noteId}`,
        body,
      )) as SwayyAPIResponse<SwayyOrderNote | { note: SwayyOrderNote }>;

      const payload = response.data ?? response.result;
      if (!response.success || !payload) {
        throw new Error(response.message || "Failed to update note");
      }

      return unwrapNote(payload);
    } catch (error) {
      throw error;
    }
  };

  deleteOrderNote = async (
    orderId: number | string,
    noteId: number | string,
  ): Promise<SwayyOrderNote> => {
    try {
      const response = (await deleteAPI(
        `/v1/orders/admin/${orderId}/notes/${noteId}`,
      )) as SwayyAPIResponse<SwayyOrderNote | { note: SwayyOrderNote }>;

      const payload = response.data ?? response.result;
      if (!response.success || !payload) {
        throw new Error(response.message || "Failed to delete note");
      }

      return unwrapNote(payload);
    } catch (error) {
      throw error;
    }
  };

  cancelOrder = async (
    orderId: number | string,
    body: { reason: string },
  ): Promise<CancelSwayyOrderResult> => {
    try {
      const response = (await postAPI(
        `/v1/orders/admin/${orderId}/cancel`,
        body,
      )) as SwayyAPIResponse<CancelSwayyOrderResult>;

      const payload = response.data ?? response.result;
      if (!response.success || !payload) {
        throw new Error(response.message || "Failed to cancel order");
      }

      return payload;
    } catch (error) {
      throw error;
    }
  };
}
